import React, { useState, useEffect } from "react";
import { Spin, Tabs, message } from "antd";
import { GiftOutlined } from "@ant-design/icons";
import { TrendingUp, BookOpen, FileText, GraduationCap } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import TopNav from "../../../components/top-nav";
import FooterDisplay from "../../../components/footer";
import AppRoutes from "../../../common/routes";
import {
  _get_profile,
  _get_ambassador_status,
  _get_ambassador_dashboard,
  _get_ambassador_schools,
} from "../../../common/axios_services";
import { fetchData, profileData } from "../../../slices/profileSlice";

import AmbassadorPerformance from "../../../components/AmbassadorPerformance";
import AmbassadorLessons from "./AmbassadorLessons";
import AmbassadorTermsDrawer from "../../../components/ambassador/AmbassadorTermsDrawer";
import AmbassadorApplicantLinksCard from "../../../components/ambassador/AmbassadorApplicantLinksCard";
import AmbassadorReferralLinksDrawer from "../../../components/ambassador/AmbassadorReferralLinksDrawer";
import AmbassadorApplicationForm from "../../../components/ambassador/AmbassadorApplicationForm";
import AmbassadorProfileEditor from "../../../components/ambassador/AmbassadorProfileEditor";
import { getReferralLink } from "../../../utils/referralHelpers";

const JoinAsAmbassador = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [schoolsList, setSchoolsList] = useState([]);
  const [promoStats, setPromoStats] = useState(null);

  const [activeTabKey, setActiveTabKey] = useState("performance");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [candidateTab, setCandidateTab] = useState(null);

  const [applicantDrawerOpen, setApplicantDrawerOpen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const fetchSchools = async () => {
    try {
      const res = await _get_ambassador_schools();
      const list = res.data?.data || res.data;
      if (Array.isArray(list) && list.length > 0) {
        setSchoolsList(list.filter((school) => school !== "Other"));
      }
    } catch (err) {
      console.error("Failed to fetch prefilled schools from backend:", err);
    }
  };

  const fetchUserProfile = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      let user = null;
      try {
        const profileResponse = await _get_profile();
        user = profileResponse.data?.data || profileResponse.data;
        if (user) {
          setUserProfile(user);
        }
      } catch (profileErr) {
        console.error("Error fetching user profile:", profileErr);
      }

      let isApproved = Boolean(
        user?.isAmbassador && (user?.status === "APPROVED" || user?.ambassadorStatus === "APPROVED")
      );

      try {
        const ambRes = await _get_ambassador_status();
        const ambData = ambRes.data?.data || ambRes.data || {};
        setUserProfile((prev) => ({
          ...prev,
          ...ambData,
        }));
        if (ambData?.isAmbassador && (ambData?.status === "APPROVED" || ambData?.ambassadorStatus === "APPROVED")) {
          isApproved = true;
        }
      } catch (ambErr) {
        console.error("Error fetching ambassador status:", ambErr);
      }

      if (isApproved) {
        try {
          const dashboardRes = await _get_ambassador_dashboard();
          setPromoStats(dashboardRes.data?.data || dashboardRes.data || null);
        } catch (dashboardErr) {
          console.error("Error fetching ambassador dashboard:", dashboardErr);
        }
      } else {
        setPromoStats(null);
      }
    } catch (err) {
      console.error("Error in fetchUserProfile:", err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchUserProfile();
    fetchSchools();

    const hash = window.location.hash;
    const search = window.location.search;
    if (
      hash.includes("onboarding") ||
      hash.includes("ambassador-profile-data-form") ||
      search.includes("tab=onboarding")
    ) {
      setActiveTabKey("onboarding");
      setTimeout(() => {
        const el = document.getElementById("ambassador-profile-data-form");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, []);

  const userId = userProfile?.id || userProfile?.accountId;
  const isApprovedAmbassador = Boolean(
    userProfile?.isAmbassador &&
    (userProfile?.status === "APPROVED" || userProfile?.ambassadorStatus === "APPROVED")
  );

  const hasSubmittedForm = Boolean(
    formSubmitted ||
    userProfile?.hasApplied ||
    userProfile?.isAmbassador ||
    (userProfile?.status && userProfile.status !== "NOT_APPLIED") ||
    (userProfile?.ambassadorStatus && userProfile.ambassadorStatus !== "NOT_APPLIED") ||
    (userProfile?.ambassadorProfile?.status && userProfile.ambassadorProfile.status !== "NOT_APPLIED")
  );

  useEffect(() => {
    if (userId && userProfile && !userProfile.hasApplied && !userProfile.isAmbassador && userProfile.status === "NOT_APPLIED") {
      localStorage.removeItem(`konnect_ambassador_form_submitted_${userId}`);
    }
    if (userId) {
      if (hasSubmittedForm) {
        setAgreedToTerms(true);
      } else {
        try {
          const saved = localStorage.getItem(`konnect_ambassador_form_draft_${userId}`);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.agreedToTerms) {
              setAgreedToTerms(true);
            }
          }
        } catch (e) {}
      }
    }
  }, [userId, userProfile, hasSubmittedForm]);

  const effectiveCandidateTab = !hasSubmittedForm
    ? "form"
    : (candidateTab !== null ? candidateTab : "lessons");

  const handleApplicationSuccess = () => {
    if (userId) {
      localStorage.setItem(`konnect_ambassador_form_submitted_${userId}`, "true");
    }
    setFormSubmitted(true);
    setCandidateTab("lessons");
    dispatch(profileData(false));
    dispatch(fetchData(false));
    fetchUserProfile();
    message.success("Application submitted successfully! Please proceed with your training lessons.");
  };

  return (
    <div className="wise9ja-content display-main-content">
      <TopNav title="Ambassador Portal" />

      <div className="pb-40 pt-4 md:pt-6">
        <div className="contain">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spin size="large" />
            </div>
          ) : isApprovedAmbassador ? (
            <div className="space-y-6">
              <Tabs
                activeKey={activeTabKey}
                onChange={(key) => setActiveTabKey(key)}
                className="custom-ambassador-tabs"
                items={[
                  {
                    key: "performance",
                    label: (
                      <span className="flex items-center gap-2 font-bold px-2 py-1">
                        <TrendingUp className="w-5 h-5" /> Performance
                      </span>
                    ),
                    children: (
                      <AmbassadorPerformance
                        ambassadorInfo={{
                          accountId: userProfile?.accountId || userProfile?.id,
                          tierName: "Verified Ambassador",
                          isApproved: isApprovedAmbassador,
                          status: userProfile?.status || userProfile?.ambassadorStatus || "APPROVED",
                          isStudent: Boolean(userProfile?.isStudent ?? userProfile?.ambassadorIsStudent),
                          school: userProfile?.school || userProfile?.ambassadorSchool || null,
                          studentNumber: userProfile?.studentNumber || userProfile?.ambassadorStudentNumber || null,
                          graduationYear: userProfile?.graduationYear || userProfile?.ambassadorGraduationYear || null,
                          topPosts:
                            userProfile?.ambassadorSubmittedPosts ||
                            userProfile?.submittedPosts ||
                            [],
                        }}
                        performanceStats={{
                          ...(promoStats?.promoStats || {}),
                          promoStartDate: promoStats?.promoStartDate,
                          promoEndDate: promoStats?.promoEndDate,
                          totalEarnings: promoStats?.promoStats?.totalEarnings || 0,
                        }}
                      />
                    ),
                  },
                  {
                    key: "onboarding",
                    label: (
                      <span className="flex items-center gap-2 font-bold px-2 py-1">
                        <GiftOutlined className="w-5 h-5" /> Onboarding & Profile Info
                      </span>
                    ),
                    children: (
                      <AmbassadorProfileEditor
                        userProfile={userProfile}
                        schoolsList={schoolsList}
                        onUpdated={fetchUserProfile}
                      />
                    ),
                  },
                  {
                    key: "lessons",
                    label: (
                      <span className="flex items-center gap-2 font-bold px-2 py-1">
                        <BookOpen className="w-5 h-5" /> Training Lessons
                      </span>
                    ),
                    children: (
                      <div className="pt-2">
                        <AmbassadorLessons
                          userProfile={userProfile}
                          initialFlowState="INTRO"
                          onCompleted={() => fetchUserProfile(true)}
                          onRefreshUser={() => fetchUserProfile(true)}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {hasSubmittedForm && Boolean(userProfile?.isStudent || userProfile?.ambassadorIsStudent || userProfile?.school || userProfile?.ambassadorSchool) && (
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex items-center justify-between gap-4 text-left">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="bg-emerald-50 text-emerald-600 p-2.5 rounded-xl flex-shrink-0 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-gilroyBold text-gray-900 text-[14px]">
                          Student Ambassador Track
                        </span>
                        <span className="font-gilroyBold text-xs px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Active
                        </span>
                      </div>
                      <p className="text-gray-500 text-[13px] font-gilroyMedium m-0 truncate">
                        {userProfile?.school || userProfile?.ambassadorSchool || "Higher Institution"}
                        {(userProfile?.graduationYear || userProfile?.ambassadorGraduationYear) ? ` • Class of ${userProfile?.graduationYear || userProfile?.ambassadorGraduationYear}` : ""}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <AmbassadorApplicantLinksCard
                referralLink={getReferralLink(userProfile?.accountId || userProfile?.referralCode || userProfile?.id, "ambassador")}
                onOpenDrawer={() => setApplicantDrawerOpen(true)}
              />

              <AmbassadorReferralLinksDrawer
                open={applicantDrawerOpen}
                onClose={() => setApplicantDrawerOpen(false)}
                accountId={userProfile?.accountId || userProfile?.id}
              />

              {hasSubmittedForm && (
                <div className="bg-gray-100/90 p-1 rounded-2xl flex gap-1 shadow-xs border border-gray-200/50">
                  <button
                    type="button"
                    onClick={() => setCandidateTab("lessons")}
                    className={`flex-1 py-2 px-3 rounded-xl font-gilroyBold text-[13px] transition-all flex items-center justify-center gap-1.5 cursor-pointer border-0 whitespace-nowrap ${
                      effectiveCandidateTab === "lessons"
                        ? "bg-white text-gray-900 shadow-xs"
                        : "bg-transparent text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    <BookOpen className={`w-4 h-4 flex-shrink-0 ${effectiveCandidateTab === "lessons" ? "text-emerald-600" : "text-gray-400"}`} />
                    <span>Lessons & Quiz</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCandidateTab("form")}
                    className={`flex-1 py-2 px-3 rounded-xl font-gilroyBold text-[13px] transition-all flex items-center justify-center gap-1.5 cursor-pointer border-0 whitespace-nowrap ${
                      effectiveCandidateTab === "form"
                        ? "bg-white text-gray-900 shadow-xs"
                        : "bg-transparent text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    <FileText className={`w-4 h-4 flex-shrink-0 ${effectiveCandidateTab === "form" ? "text-emerald-600" : "text-gray-400"}`} />
                    <span>Review Application</span>
                  </button>
                </div>
              )}

              {effectiveCandidateTab === "lessons" && hasSubmittedForm ? (
                <AmbassadorLessons
                  userProfile={userProfile}
                  initialFlowState={userProfile?.trainingCompleted ? undefined : (formSubmitted ? "INTRO" : undefined)}
                  onCompleted={() => fetchUserProfile(true)}
                  onRefreshUser={() => fetchUserProfile(true)}
                />
              ) : (
                <AmbassadorApplicationForm
                  userProfile={userProfile}
                  schoolsList={schoolsList}
                  onSuccess={handleApplicationSuccess}
                  onOpenTerms={() => setShowTermsModal(true)}
                  agreedToTerms={agreedToTerms}
                  setAgreedToTerms={setAgreedToTerms}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <AmbassadorTermsDrawer
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setAgreedToTerms(true);
          setShowTermsModal(false);
        }}
        userProfile={userProfile}
      />

      <FooterDisplay activeTab="profile" />
    </div>
  );
};

export default JoinAsAmbassador;
